import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { KEYCLOAK_CONFIG, KeycloakConfig } from '@angular-workspace/core';
import { AuthApiService } from '@angular-workspace/services';
import { clearManageDelegatesTabStorage } from '@angular-workspace/feature';

/** Keys to clear from localStorage on logout */
const TOKEN_KEYS_TO_CLEAR = ['iudx-ui-cat-auth-token', 'ai-sandbox-cat-auth-token'];

/**
 * Service that centralizes logout logic and ensures the access token is invalidated.
 *
 * Note: JWT access tokens are stateless - the control plane API validates them by
 * signature + expiry only. It does NOT check Keycloak's revocation list. So even
 * after we revoke the token on Keycloak, the token may still work until it expires
 * unless the control plane uses token introspection. To fully invalidate tokens
 * immediately, the control plane backend must implement token introspection or
 * maintain its own session/blacklist via a logout endpoint.
 */
@Injectable({
  providedIn: 'root',
})
export class LogoutService {
  constructor(
    private keycloak: KeycloakService,
    private http: HttpClient,
    private authApiService: AuthApiService,
    @Inject(KEYCLOAK_CONFIG) private keycloakConfig: KeycloakConfig
  ) {}

  /**
   * Performs full logout: notifies control plane, revokes token on Keycloak,
   * clears local storage, then logs out.
   */
  async logout(redirectUri: string = window.location.origin): Promise<void> {
    // 1. Notify control plane to invalidate session (if it has a logout endpoint)
    try {
      await this.authApiService.logoutSession();
    } catch (err) {
      // Control plane may not have logout endpoint
    }

    // 2. Revoke the access token on Keycloak
    try {
      await this.revokeToken();
    } catch (err) {
      console.warn('Token revocation failed (proceeding with logout):', err);
    }

    // 3. Clear any locally stored tokens + UI session prefs
    this.clearStoredTokens();
    clearManageDelegatesTabStorage();

    // 4. Set logout flag for guard/auth listener
    sessionStorage.setItem('logout-redirect', 'true');

    // 5. Perform Keycloak logout (clears session and redirects)
    await this.keycloak.logout(redirectUri);
  }

  /**
   * Calls Keycloak's token revocation endpoint to invalidate the access token.
   */
  private async revokeToken(): Promise<void> {
    const isLoggedIn = await this.keycloak.isLoggedIn();
    if (!isLoggedIn) return;

    let token: string | undefined;
    let refreshToken: string | undefined;

    try {
      token = await this.keycloak.getToken();
      const kc = this.keycloak.getKeycloakInstance();
      refreshToken = kc?.refreshToken;
    } catch {
      return;
    }

    if (!token) return;

    const { url, realm, clientId } = this.keycloakConfig;
    const revokeUrl = `${url}/realms/${realm}/protocol/openid-connect/revoke`;
    const body = new URLSearchParams({
      client_id: clientId || 'angular-client',
      token,
      token_type_hint: 'access_token',
    });

    console.log('[LogoutService] Revoking access token...');
    await firstValueFrom(
      this.http.post(revokeUrl, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        responseType: 'text',
      })
    );
    console.log('[LogoutService] Access token revoked on Keycloak');

    // Also revoke refresh token if available
    if (refreshToken) {
      const refreshBody = new URLSearchParams({
        client_id: clientId || 'angular-client',
        token: refreshToken,
        token_type_hint: 'refresh_token',
      });
      try {
        await firstValueFrom(
          this.http.post(revokeUrl, refreshBody.toString(), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            responseType: 'text',
          })
        );
      } catch {
        // Ignore refresh revocation errors
      }
    }
  }

  /**
   * Clears auth-related tokens from localStorage.
   */
  private clearStoredTokens(): void {
    for (const key of TOKEN_KEYS_TO_CLEAR) {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore
      }
    }
  }
}
